import React, { useState } from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { HeatmapTableCell } from 'libs/end-to-end/src/heatmap-analysis';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

interface TableGridTooltipProps {
  cell: HeatmapTableCell;
  getDeepDiveLink: (cell: HeatmapTableCell) => Promise<string | undefined>;
}

/**
 * A tooltip component that displays detailed information about a table grid cell.
 * Shows disparity values, sample sizes, and confidence levels for the selected metric and group.
 * Includes a deep dive link for more detailed analysis.
 *
 * @component
 * @param {TableGridTooltipProps} props - Component props
 * @returns {React.ReactElement} Rendered tooltip
 */
export const TableGridTooltip: React.FC<TableGridTooltipProps> = ({
  cell,
  getDeepDiveLink,
}) => {
  const language = useLanguage();

  // Calculate the controlled disparity, sample size, and confidence level
  const controlledDisparity = Math.round((cell.disparityValue ?? 0) * 100);
  const people = cell.sampleSize ?? 0;
  const confidence = Math.round((cell.confidenceLevel ?? 0) * 100);

  const [isLoadingLinkToDeepDive, setIsLoadingLinkToDeepDive] = useState(false);
  const [linkToDeepDive, setLinkToDeepDive] = useState<undefined | string>(
    undefined,
  );

  useAsyncEffect(async () => {
    setIsLoadingLinkToDeepDive(true);
    const deepDiveLink = await getDeepDiveLink(cell);
    setIsLoadingLinkToDeepDive(false);
    setLinkToDeepDive(deepDiveLink);
  }, [cell, getDeepDiveLink]);

  return (
    <>
      <div className="flex justify-between items-start mb-3">
        <S2Typography
          variant="headline-6-alt"
          textColorVariant="gray-900"
          translatedContent={language.exploreHeatmapAnalysisToastTitle({
            metricDisplayName: cell.metricLabel,
            levelDisplayName: cell.group,
          })}
        />
      </div>
      <div
        className={`space-y-2 border-l-2 ${cell.disparityValue === null ? 'border-gray-300' : 'border-green-500'}`}
      >
        {cell.disparityValue === null ? (
          <div className="pl-2">
            <S2Typography
              variant="body-7"
              textColorVariant="gray-600"
              translatedContent={language.exploreHeatmapAnalysisToastNoData()}
            />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1 pl-2">
              <span className="font-medium">{controlledDisparity}%</span>
              <S2Typography
                variant="body-7"
                textColorVariant="gray-600"
                translatedContent={language.exploreHeatmapAnalysisToastDisparity()}
              />
            </div>
            <div className="flex items-center gap-1 pl-2">
              <span className="font-medium">{people}</span>
              <S2Typography
                variant="body-7"
                textColorVariant="gray-600"
                translatedContent={language.exploreHeatmapAnalysisToastPeople()}
              />
            </div>
          </>
        )}
      </div>
      {cell.disparityValue !== null && (
        <div className="flex items-center gap-1 pt-1">
          <S2Typography
            variant="body-7"
            textColorVariant="gray-600"
            translatedContent={language.exploreHeatmapAnalysisToastConfidence()}
          />
          <span className="font-medium">{confidence}%</span>
        </div>
      )}
      {linkToDeepDive && (
        <TouchTarget
          content={() => {
            return (
              <S2Typography
                variant="headline-6-alt-underline"
                textColorVariant="royal-600"
                translatedContent={language.exploreHeatmapAnalysisToastLearnMore()}
              />
            );
          }}
          behavior={{
            type: 'href',
            href: linkToDeepDive,
          }}
        />
      )}
      {isLoadingLinkToDeepDive && (
        <div className="flex items-center h-6">
          <Spinner size={16} />
        </div>
      )}
    </>
  );
};
